"use client";

import { useState } from "react";
import {
  Check,
  Info,
  Minus,
  Pencil,
  PhoneForwarded,
  Plus,
  RotateCcw,
  Users,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import type { Agent } from "@/lib/api";
import { DEFAULT_AGENT_PROMPTS } from "@/lib/api";
import { AgentPromptEditDialog } from "@/components/agent-prompt-edit-dialog";
import { Select } from "@/components/ui/select";

const LINE_MAX = 200;
const IDLE_LINE_MAX = 280;
const CLASSIFIER_MAX = 2000;
const IDLE_SEC_MIN = 3;
const IDLE_SEC_MAX = 60;
const IDLE_NUDGE_MIN = 1;
const IDLE_NUDGE_MAX = 3;

/** Preview-only destinations (MVP types: queue + specific operator). Not persisted. */
const HANDOFF_DESTINATIONS = [
  {
    id: "queue-patient-support",
    label: "Patient support queue",
    kind: "queue" as const,
    description: "Queue",
  },
  {
    id: "queue-billing",
    label: "Patient billing queue",
    kind: "queue" as const,
    description: "Queue",
  },
  {
    id: "operator-demo",
    label: "Priya Raman",
    kind: "operator" as const,
    description: "Available operator",
    avatar: { initials: "PR", online: true },
  },
];

const HANDOFF_UNAVAILABLE = [
  { id: "return_agent", label: "Return control to agent" },
  { id: "try_other", label: "Try another destination" },
  { id: "keep_waiting", label: "Keep waiting in queue" },
  { id: "offer_callback", label: "Offer a callback" },
  { id: "end_message", label: "End session with message" },
] as const;

function clampIdleSeconds(v: unknown, fallback = 8): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(IDLE_SEC_MIN, Math.min(IDLE_SEC_MAX, Math.round(n)));
}

function clampIdleMaxNudges(v: unknown, fallback = 1): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(IDLE_NUDGE_MIN, Math.min(IDLE_NUDGE_MAX, Math.round(n)));
}

function FieldHealth({ value, max }: { value: string; max: number }) {
  const len = value.length;
  const ok = len > 0 && len <= max;
  return (
    <div className="agent-conv-field-foot">
      {ok ? (
        <span className="agent-conv-looks-good">
          <Check size={13} strokeWidth={2.5} aria-hidden />
          Looks good
        </span>
      ) : len > max ? (
        <span className="agent-conv-looks-bad">Over limit</span>
      ) : (
        <span className="agent-conv-looks-muted">Required</span>
      )}
      <span className={`agent-conv-count${len > max ? " is-over" : ""}`}>
        {len.toLocaleString()} / {max.toLocaleString()}
      </span>
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit: string;
  onChange: (n: number) => void;
}) {
  return (
    <label className="agent-limits-slider">
      <div className="agent-limits-slider-head">
        <b>{label}</b>
        <span className="agent-limits-range">
          {min}–{max} {unit}
        </span>
      </div>
      <div className="agent-limits-slider-row">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label={label}
        />
        <div className="agent-limits-num">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return;
              onChange(Number(raw));
            }}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (!Number.isFinite(n)) onChange(min);
              else onChange(Math.max(min, Math.min(max, Math.round(n))));
            }}
            aria-label={`${label} value`}
          />
          <em>{unit === "minutes" ? "min" : unit === "seconds" ? "sec" : unit}</em>
        </div>
      </div>
    </label>
  );
}

type Props = {
  draft: Agent;
  onChange: (next: Agent) => void;
};

export function AgentEditCallControlPanel({ draft, onChange }: Props) {
  const [editHangup, setEditHangup] = useState(false);
  const [editAffirm, setEditAffirm] = useState(false);
  const [handoffDest, setHandoffDest] = useState(HANDOFF_DESTINATIONS[0]?.id ?? "");
  const [handoffUnavailable, setHandoffUnavailable] = useState("return_agent");

  const confirm = draft.hangupConfirmText ?? DEFAULT_AGENT_PROMPTS.hangupConfirmText;
  const goodbye = draft.hangupGoodbyeText ?? DEFAULT_AGENT_PROMPTS.hangupGoodbyeText;
  const hangupPrompt = draft.hangupPrompt ?? DEFAULT_AGENT_PROMPTS.hangupPrompt;
  const affirmPrompt = draft.affirmPrompt ?? DEFAULT_AGENT_PROMPTS.affirmPrompt;
  const idleOn = draft.idleEnabled !== false;
  const idleSec = clampIdleSeconds(draft.idleSeconds, 8);
  const idleNudges = clampIdleMaxNudges(draft.idleMaxNudges, 1);
  const idlePrompt = draft.idlePrompt ?? DEFAULT_AGENT_PROMPTS.idlePrompt;
  const idleGoodbye = draft.idleGoodbyeText ?? DEFAULT_AGENT_PROMPTS.idleGoodbyeText;

  function patch(partial: Partial<Agent>) {
    onChange({ ...draft, ...partial });
  }

  return (
    <div className="agent-conv agent-call">
      <section className="agent-limits-card">
        <header className="agent-limits-card-head">
          <div>
            <h3 className="agent-conv-h">Hangup flow</h3>
            <p className="agent-limits-lead">
              When the user wants to leave, confirm first, then speak goodbye before ending.
            </p>
          </div>
        </header>

        <label className="agent-conv-field">
          <div className="agent-conv-field-head">
            <div>
              <b>Confirm line</b>
              <span>Spoken after hangup intent is detected — wait for yes/no.</span>
            </div>
          </div>
          <input
            className="agent-conv-input"
            value={confirm}
            maxLength={LINE_MAX}
            onChange={(e) => patch({ hangupConfirmText: e.target.value })}
            placeholder={DEFAULT_AGENT_PROMPTS.hangupConfirmText}
          />
          <FieldHealth value={confirm} max={LINE_MAX} />
        </label>

        <label className="agent-conv-field">
          <div className="agent-conv-field-head">
            <div>
              <b>Goodbye line</b>
              <span>Spoken after confirm — must finish before the session ends.</span>
            </div>
          </div>
          <input
            className="agent-conv-input"
            value={goodbye}
            maxLength={LINE_MAX}
            onChange={(e) => patch({ hangupGoodbyeText: e.target.value })}
            placeholder={DEFAULT_AGENT_PROMPTS.hangupGoodbyeText}
          />
          <FieldHealth value={goodbye} max={LINE_MAX} />
        </label>

        <div className="agent-limits-note" role="note">
          <Info size={15} strokeWidth={2} aria-hidden />
          <p>
            Goodbye audio always plays in full. Interrupting during confirm answers the new
            question instead of hanging up.
          </p>
        </div>
      </section>

      <section className="agent-limits-card">
        <header className="agent-limits-card-head">
          <div>
            <h3 className="agent-conv-h">Classifiers</h3>
            <p className="agent-limits-lead">
              AUX YES/NO prompts for hangup intent and confirm. Edit only when tuning false
              positives.
            </p>
          </div>
        </header>

        <div className="agent-eot-prompt-card">
          <div className="agent-eot-prompt-card-head">
            <div>
              <b>Hangup intent</b>
              <span>
                {hangupPrompt.length.toLocaleString()} / {CLASSIFIER_MAX.toLocaleString()}
              </span>
            </div>
            <div className="agent-eot-prompt-actions">
              <button
                type="button"
                className="agent-eot-edit-btn"
                aria-label="Reset hangup intent"
                title="Reset to default"
                onClick={() => patch({ hangupPrompt: DEFAULT_AGENT_PROMPTS.hangupPrompt })}
              >
                <RotateCcw size={14} />
              </button>
              <button
                type="button"
                className="agent-eot-edit-btn"
                aria-label="Edit hangup intent"
                onClick={() => setEditHangup(true)}
              >
                <Pencil size={14} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className="agent-eot-prompt-preview-btn"
            onClick={() => setEditHangup(true)}
          >
            <pre className="agent-eot-prompt-preview">{hangupPrompt}</pre>
          </button>
        </div>

        <div className="agent-eot-prompt-card">
          <div className="agent-eot-prompt-card-head">
            <div>
              <b>Confirm affirm</b>
              <span>
                {affirmPrompt.length.toLocaleString()} / {CLASSIFIER_MAX.toLocaleString()}
              </span>
            </div>
            <div className="agent-eot-prompt-actions">
              <button
                type="button"
                className="agent-eot-edit-btn"
                aria-label="Reset confirm affirm"
                title="Reset to default"
                onClick={() => patch({ affirmPrompt: DEFAULT_AGENT_PROMPTS.affirmPrompt })}
              >
                <RotateCcw size={14} />
              </button>
              <button
                type="button"
                className="agent-eot-edit-btn"
                aria-label="Edit confirm affirm"
                onClick={() => setEditAffirm(true)}
              >
                <Pencil size={14} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className="agent-eot-prompt-preview-btn"
            onClick={() => setEditAffirm(true)}
          >
            <pre className="agent-eot-prompt-preview">{affirmPrompt}</pre>
          </button>
        </div>
      </section>

      <section className={`agent-limits-card${idleOn ? "" : " is-disabled"}`}>
        <header className="agent-limits-card-head">
          <div>
            <h3 className="agent-conv-h">Idle presence</h3>
            <p className="agent-limits-lead">
              Detect silence and gently nudge the user before ending the call.
            </p>
          </div>
          <Switch
            checked={idleOn}
            onCheckedChange={(v) => patch({ idleEnabled: v })}
            aria-label="Idle presence"
          />
        </header>

        <div className="agent-limits-idle-body" aria-hidden={!idleOn}>
          <fieldset disabled={!idleOn} className="agent-limits-fieldset">
            <SliderRow
              label="Silence before nudge"
              value={idleSec}
              min={IDLE_SEC_MIN}
              max={IDLE_SEC_MAX}
              unit="seconds"
              onChange={(n) => patch({ idleSeconds: clampIdleSeconds(n, 8) })}
            />

            <div className="agent-limits-stepper-field">
              <div className="agent-limits-slider-head">
                <b>Nudges before goodbye</b>
                <span className="agent-limits-range">
                  {IDLE_NUDGE_MIN}–{IDLE_NUDGE_MAX}
                </span>
              </div>
              <div className="agent-limits-stepper" role="group" aria-label="Nudges before goodbye">
                <button
                  type="button"
                  aria-label="Decrease nudges"
                  disabled={idleNudges <= IDLE_NUDGE_MIN}
                  onClick={() => patch({ idleMaxNudges: clampIdleMaxNudges(idleNudges - 1, 1) })}
                >
                  <Minus size={14} strokeWidth={2.5} />
                </button>
                <span aria-live="polite">{idleNudges}</span>
                <button
                  type="button"
                  aria-label="Increase nudges"
                  disabled={idleNudges >= IDLE_NUDGE_MAX}
                  onClick={() => patch({ idleMaxNudges: clampIdleMaxNudges(idleNudges + 1, 1) })}
                >
                  <Plus size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>

            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>Idle nudge line</b>
                  <span>Spoken after silence — first nudge cancels pending reply work.</span>
                </div>
              </div>
              <input
                className="agent-conv-input"
                value={idlePrompt}
                maxLength={IDLE_LINE_MAX}
                onChange={(e) => patch({ idlePrompt: e.target.value })}
                placeholder={DEFAULT_AGENT_PROMPTS.idlePrompt}
              />
            </label>

            <label className="agent-conv-field">
              <div className="agent-conv-field-head">
                <div>
                  <b>Idle goodbye line</b>
                  <span>Spoken once, then the session ends.</span>
                </div>
              </div>
              <input
                className="agent-conv-input"
                value={idleGoodbye}
                maxLength={IDLE_LINE_MAX}
                onChange={(e) => patch({ idleGoodbyeText: e.target.value })}
                placeholder={DEFAULT_AGENT_PROMPTS.idleGoodbyeText}
              />
            </label>

            <div className="agent-limits-note" role="note">
              <Info size={15} strokeWidth={2} aria-hidden />
              <p>
                The first nudge cancels pending LLM tasks and hangups. Silence is measured after the
                agent finishes speaking, not while TTS is still playing.
              </p>
            </div>
          </fieldset>
        </div>
      </section>

      <section className="agent-limits-card agent-handoff is-coming-soon" aria-labelledby="agent-handoff-title">
        <div className="agent-coming-soon-banner" role="status">
          <span className="agent-coming-soon-badge">Coming soon</span>
          <p>
            Preview only — handoff is not wired yet. Destinations will be managed under Settings →
            Handoff destinations.
          </p>
        </div>

        <fieldset disabled className="agent-handoff-fieldset">
          <header className="agent-limits-card-head agent-handoff-head">
            <div className="agent-handoff-title-row">
              <span className="agent-handoff-icon" aria-hidden>
                <PhoneForwarded size={18} strokeWidth={1.75} />
              </span>
              <div>
                <h3 className="agent-conv-h" id="agent-handoff-title">
                  Human handoff
                </h3>
                <p className="agent-limits-lead">
                  Allow the agent to transfer the session to a configured support destination.
                </p>
              </div>
            </div>
            <Switch checked disabled aria-label="Human handoff" />
          </header>

          <div className="agent-handoff-grid">
            <div className="agent-handoff-field">
              <Select
                label="Destination"
                placeholder="Select a destination"
                ariaLabel="Handoff destination"
                groupBy
                searchable
                searchPlaceholder="Search destinations…"
                footerHint="Type to filter destinations"
                clearable
                value={handoffDest || null}
                onChange={(v) => setHandoffDest(v ?? "")}
                options={HANDOFF_DESTINATIONS.map((d) => ({
                  value: d.id,
                  label: d.label,
                  description: d.description,
                  group: d.kind === "queue" ? "Queues" : "Operators",
                  badge: d.kind === "queue" ? "Queue" : undefined,
                  icon: d.kind === "queue" ? <Users size={16} /> : undefined,
                  avatar: d.avatar,
                }))}
              />
            </div>

            <div className="agent-handoff-field">
              <span className="agent-handoff-label" id="agent-handoff-mode-label">
                Handoff mode
              </span>
              <div
                className="agent-handoff-mode"
                role="group"
                aria-labelledby="agent-handoff-mode-label"
              >
                <button type="button" className="is-active" aria-pressed="true">
                  Warm handoff
                </button>
                <button type="button" aria-pressed="false">
                  Direct transfer
                </button>
              </div>
              <p className="agent-handoff-mode-hint">
                The agent stays with the user until the destination accepts a warm handoff.
              </p>
            </div>

            <label className="agent-handoff-field">
              <span className="agent-handoff-label">If unavailable</span>
              <Select
                ariaLabel="If handoff destination unavailable"
                searchable={false}
                value={handoffUnavailable}
                onChange={(v) => setHandoffUnavailable(v ?? "return_agent")}
                options={HANDOFF_UNAVAILABLE.map((o) => ({
                  value: o.id,
                  label: o.label,
                }))}
              />
            </label>
          </div>
        </fieldset>
      </section>

      <AgentPromptEditDialog
        open={editHangup}
        onOpenChange={setEditHangup}
        title="Hangup intent classifier"
        description="Binary YES/NO prompt for detecting that the user wants to end the call."
        value={hangupPrompt}
        max={CLASSIFIER_MAX}
        defaultValue={DEFAULT_AGENT_PROMPTS.hangupPrompt}
        onSave={(next) => patch({ hangupPrompt: next })}
      />
      <AgentPromptEditDialog
        open={editAffirm}
        onOpenChange={setEditAffirm}
        title="Confirm affirm classifier"
        description="Binary YES/NO prompt for confirming the user still wants to hang up."
        value={affirmPrompt}
        max={CLASSIFIER_MAX}
        defaultValue={DEFAULT_AGENT_PROMPTS.affirmPrompt}
        onSave={(next) => patch({ affirmPrompt: next })}
      />
    </div>
  );
}
